// The logframe: results, indicators and readings (SPEC-v2 L1-L3; API.md 7.7,
// endpoints 77-80; ARCHITECTURE 19). Progress is in ../progress.ts.
import { z } from "zod";
import {
  INDICATOR_AUTO_SOURCES,
  INDICATOR_KINDS,
  RESULT_LEVELS,
  RESULT_LEVELS_MAX,
  RESULT_LEVELS_MIN,
  levelNoOf,
  type ContractStatus,
  autoSourceFitsKind,
  type IndicatorAutoSource,
  type IndicatorKind,
  type TorStatus,
} from "../enums";
import { LIMITS } from "../limits";
import { type ActorRef, httpUrlOrEmpty, isoDate, optionalText, requiredText, uuid } from "./common";

// ---------- Requests ----------

/**
 * The short name of a result or an indicator: a few plain words. Left out =
 * keep the one saved; empty or null = no short name.
 */
export const logframeShortName = optionalText(LIMITS.logframeShortName, "Short name")
  .nullable()
  .optional()
  .transform((v) => (v === undefined ? undefined : v || null));

/** −1e12 … 1e12 with at most 2 decimals. */
export const indicatorValue = z
  .number({ invalid_type_error: "Enter a number." })
  .min(-LIMITS.indicatorValueAbsMax, "The number is too small.")
  .max(LIMITS.indicatorValueAbsMax, "The number is too large.")
  .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, "Use at most 2 decimals.");

/** The earliest and latest year a baseline (LF2) can name. */
export const BASELINE_YEAR_MIN = 1950;
export const BASELINE_YEAR_MAX = 2100;
export const baselineYear = z
  .number({ invalid_type_error: "Enter the year as four digits." })
  .int("Enter the year as four digits.")
  .min(BASELINE_YEAR_MIN, `Enter a year from ${BASELINE_YEAR_MIN}.`)
  .max(BASELINE_YEAR_MAX, `Enter a year up to ${BASELINE_YEAR_MAX}.`);

export const indicatorInput = z
  .object({
    /** uuid v4, generated by the client for new rows */
    id: uuid,
    code: optionalText(LIMITS.resultCode, "Code").default(""),
    name: requiredText(LIMITS.indicatorName, "Indicator"),
    shortName: logframeShortName,
    unit: optionalText(LIMITS.indicatorUnit, "Unit").default(""),
    baseline: indicatorValue.nullable().default(null),
    /** LF2: the baseline's year and the baseline in words; left out = keep the saved ones. */
    baselineYear: baselineYear.nullable().optional(),
    baselineNote: optionalText(1000, "Baseline note").optional(),
    target: indicatorValue.nullable().default(null),
    targetDate: isoDate.nullable().default(null),
    meansOfVerification: optionalText(LIMITS.meansOfVerification, "Means of verification").default(""),
    /** null = readings typed by hand */
    autoSource: z.enum(INDICATOR_AUTO_SOURCES).nullable().default(null),
    /**
     * R3: count, share from answers or reading. Left out = what the automatic
     * source implies, else the kind already saved (a new indicator: reading).
     */
    kind: z.enum(INDICATOR_KINDS).optional(),
  })
  .strict()
  .refine((i) => i.kind === undefined || autoSourceFitsKind(i.kind, i.autoSource), {
    message: "This automatic source does not fit the kind of indicator.",
    path: ["autoSource"],
  });
export type IndicatorInput = z.input<typeof indicatorInput>;

/** A result level: 1 is the top of the ladder, up to 5. */
export const resultLevelNo = z
  .number({ invalid_type_error: "Choose the level." })
  .int("Choose the level.")
  .min(1, "Choose the level.")
  .max(RESULT_LEVELS_MAX, `A logframe has at most ${RESULT_LEVELS_MAX} levels.`);

export const resultInput = z
  .object({
    /** uuid v4, generated by the client for new rows, so a new output can name a new outcome as parent */
    id: uuid,
    /** 1 = the top level (R1). */
    levelNo: resultLevelNo.optional(),
    /** Before R1: impact, outcome or output (levels 1 to 3). Used when levelNo is left out. */
    level: z.enum(RESULT_LEVELS).optional(),
    code: optionalText(LIMITS.resultCode, "Code").default(""),
    statement: requiredText(LIMITS.resultStatement, "Statement"),
    shortName: logframeShortName,
    parentId: uuid.nullable().default(null),
    indicators: z.array(indicatorInput).default([]),
  })
  .strict()
  .refine((r) => r.levelNo !== undefined || r.level !== undefined, { message: "Choose the level.", path: ["levelNo"] })
  .transform(({ level, levelNo, ...r }) => ({ ...r, levelNo: levelNo ?? levelNoOf(level!) }));
export type ResultInput = z.input<typeof resultInput>;

/**
 * 78. PUT /api/contracts/:id/logframe: the whole structure, in order.
 * Structure problems answer 400 LOGFRAME_INVALID with details.problems
 * (see `logframeProblems`); zod only checks each row.
 */
export const putLogframeBody = z.object({ results: z.array(resultInput) }).strict();
export type PutLogframeBody = z.input<typeof putLogframeBody>;
export type PutLogframeInput = z.output<typeof putLogframeBody>;

/**
 * The names of a project's result levels, top first: 2 to 5 names
 * (PUT /logframe/levels). Renaming is free; a level in use cannot be removed.
 */
export const resultLevelNamesBody = z
  .object({
    names: z
      .array(requiredText(LIMITS.resultLevelName, "Level name"))
      .min(RESULT_LEVELS_MIN, `A logframe has at least ${RESULT_LEVELS_MIN} levels.`)
      .max(RESULT_LEVELS_MAX, `A logframe has at most ${RESULT_LEVELS_MAX} levels.`)
      .refine((a) => new Set(a.map((x) => x.toLowerCase())).size === a.length, "Give each level its own name."),
  })
  .strict();
export type ResultLevelNamesBody = z.input<typeof resultLevelNamesBody>;

/**
 * POST /api/p/:id/logframe/results/:resultId/indicators: one new indicator of
 * a result, from the Logframe page. Code left out = the next code
 * (`nextIndicatorCode`); kind left out = what the automatic source implies,
 * else a reading.
 */
export const newIndicatorBody = z
  .object({
    code: optionalText(LIMITS.resultCode, "Code").optional(),
    name: requiredText(LIMITS.indicatorName, "Indicator"),
    shortName: logframeShortName,
    unit: optionalText(LIMITS.indicatorUnit, "Unit").default(""),
    baseline: indicatorValue.nullable().default(null),
    /** LF2: the year the baseline was measured, and the baseline in words. */
    baselineYear: baselineYear.nullable().default(null),
    baselineNote: optionalText(1000, "Baseline note").default(""),
    target: indicatorValue.nullable().default(null),
    targetDate: isoDate.nullable().default(null),
    meansOfVerification: optionalText(LIMITS.meansOfVerification, "Means of verification").default(""),
    kind: z.enum(INDICATOR_KINDS).optional(),
    autoSource: z.enum(INDICATOR_AUTO_SOURCES).nullable().default(null),
    /** LF7: the funder indicator(s) it is reported as ("FPI 8b"), from the list or typed. */
    funderCode: optionalText(40, "Funder indicator").default(""),
    /** LF17: a share from feedback counted as a percentage or as people; LF6: the fewest answers that count. */
    shareAs: z.enum(["pct", "people"]).default("pct"),
    minAnswers: z.number().int().min(1, "Enter at least 1.").max(1000, "Enter 1000 or fewer.").default(5),
  })
  .strict()
  .refine((i) => i.kind === undefined || autoSourceFitsKind(i.kind, i.autoSource), {
    message: "This automatic source does not fit the kind of indicator.",
    path: ["autoSource"],
  });
export type NewIndicatorBody = z.input<typeof newIndicatorBody>;

/**
 * POST /api/p/:id/logframe/results: one new result, from the Logframe page.
 * parentId null = no result above it. The level must be below the parent's
 * and within the project's levels (`logframeProblems`). Code left out = the
 * next code (`nextResultCode`).
 */
export const newResultBody = z
  .object({
    code: optionalText(LIMITS.resultCode, "Code").optional(),
    statement: requiredText(LIMITS.resultStatement, "Statement"),
    shortName: logframeShortName,
    levelNo: resultLevelNo,
    parentId: uuid.nullable().default(null),
  })
  .strict();
export type NewResultBody = z.input<typeof newResultBody>;

// ---------- Suggested codes ----------

/** The number part at the end of a code: "R1.2" -> "1.2", "OC1" -> "1", "IM" -> null. */
const codeNumber = (code: string): string | null => /\d+(?:\.\d+)*$/.exec(code.trim())?.[0] ?? null;

/** The code after this one: "1.2" -> "1.3", "OC1.a" -> "OC1.b", "I1.2.3" -> "I1.2.4"; null when it ends in neither. */
export function bumpCode(code: string): string | null {
  const c = code.trim();
  const n = /^(.*?)(\d+)$/.exec(c);
  if (n) return `${n[1]}${String(Number(n[2]) + 1).padStart(n[2].length, "0")}`;
  const l = /^(.*[^A-Za-z])([A-Ya-y])$/.exec(c);
  if (l) return `${l[1]}${String.fromCharCode(l[2].charCodeAt(0) + 1)}`;
  return null;
}

/**
 * How a code is formed from its owner's code, learnt from an example:
 * child "I1.2.3" of "R1.2" = prefix "I", then 1.2, then "." and a number.
 */
function learnPattern(child: string, owner: string): { prefix: string; sep: string; letter: boolean } | null {
  const num = codeNumber(owner);
  if (!num) return null;
  const m = new RegExp(`^([A-Za-z]*)${num.replace(/\./g, "\\.")}([.\\-/]?)(\\d+|[A-Za-z])$`).exec(child.trim());
  return m ? { prefix: m[1], sep: m[2], letter: /[A-Za-z]/.test(m[3]) } : null;
}

/** The first code from `start` on (bumping) that nobody uses yet. */
function firstFree(start: string, taken: Set<string>): string {
  let code: string | null = start;
  for (let i = 0; code && i < 200; i++) {
    if (!taken.has(code.toUpperCase())) return code;
    code = bumpCode(code);
  }
  return "";
}

type CodeResult = { id: string; code: string; parentId?: string | null; levelNo?: number | null };
type CodeIndicator = { code: string; resultId: string };

/**
 * The code to suggest for a new indicator of `result`, following the project's
 * pattern: after the result's last indicator ("1.2" -> "1.3"); else like the
 * indicators of another result ("I2.1" of R2 -> "I1.2.1" for R1.2); else the
 * result's number and ".1". "" when nothing fits.
 */
export function nextIndicatorCode(result: CodeResult, indicators: readonly CodeIndicator[], results: readonly CodeResult[]): string {
  const taken = new Set(indicators.map((i) => i.code.trim().toUpperCase()).filter(Boolean));
  const own = indicators.filter((i) => i.resultId === result.id && i.code.trim());
  for (const i of [...own].reverse()) {
    const next = bumpCode(i.code);
    if (next) return firstFree(next, taken);
  }
  const num = codeNumber(result.code);
  // LF17: a result code without a number ("OO", "Impact") gets its own code and ".1".
  if (!num) return result.code.trim() ? firstFree(`${result.code.trim()}.1`, taken) : "";
  for (const i of indicators) {
    const owner = results.find((r) => r.id === i.resultId);
    const p = owner ? learnPattern(i.code, owner.code) : null;
    if (p) return firstFree(`${p.prefix}${num}${p.sep}${p.letter ? "a" : "1"}`, taken);
  }
  return firstFree(`${num}.1`, taken);
}

/**
 * The code to suggest for a new result under `parentId` (null = none) at
 * `levelNo`: after its last sibling ("R2" -> "R3", "OP1.1" -> "OP1.2"); else
 * like a result under another parent ("OP1.1" under OC1 -> "OP2.1" under OC2);
 * else the parent's code and ".1". "" when nothing fits.
 */
export function nextResultCode(parentId: string | null, levelNo: number, results: readonly CodeResult[]): string {
  const taken = new Set(results.map((r) => r.code.trim().toUpperCase()).filter(Boolean));
  const siblings = results.filter((r) => (r.parentId ?? null) === parentId && (parentId !== null || r.levelNo === levelNo) && r.code.trim());
  for (const r of [...siblings].reverse()) {
    const next = bumpCode(r.code);
    if (next) return firstFree(next, taken);
  }
  const parent = parentId ? results.find((r) => r.id === parentId) : undefined;
  if (!parent) return "";
  const num = codeNumber(parent.code);
  if (num) {
    for (const r of results) {
      if (r.levelNo !== levelNo || !r.parentId) continue;
      const owner = results.find((x) => x.id === r.parentId);
      const p = owner ? learnPattern(r.code, owner.code) : null;
      if (p) return firstFree(`${p.prefix}${num}${p.sep}${p.letter ? "a" : "1"}`, taken);
    }
  }
  return parent.code.trim() ? firstFree(`${parent.code.trim()}.1`, taken) : "";
}

/**
 * The structure checks of ARCHITECTURE 19.1 (R1: levels by number), as plain
 * sentences: a result sits only under a result of a higher level (a smaller
 * number), so the top level has no parent; every parentId is in the body;
 * no level beyond the project's last; ids are unique; at most 60 results and
 * 150 indicators. `levelNames` are the project's names (default: 5 levels allowed).
 */
export function logframeProblems(
  results: readonly { id: string; levelNo: number; code: string; parentId: string | null; indicators: readonly { id: string }[] }[],
  levelNames?: readonly string[],
): string[] {
  const problems: string[] = [];
  const byId = new Map(results.map((r) => [r.id, r]));
  const levelOf = (n: number) => levelNames?.[n - 1] ?? `level ${n}`;
  const name = (r: { code: string; levelNo: number }) => (r.code ? `${r.code}` : `A result at ${levelOf(r.levelNo).toLowerCase()}`);
  if (byId.size !== results.length) problems.push("Two results have the same id.");
  const indicatorIds = results.flatMap((r) => r.indicators.map((i) => i.id));
  if (new Set(indicatorIds).size !== indicatorIds.length) problems.push("Two indicators have the same id.");
  if (results.length > LIMITS.resultsMax) problems.push(`A logframe can have at most ${LIMITS.resultsMax} results.`);
  if (indicatorIds.length > LIMITS.indicatorsMax)
    problems.push(`A logframe can have at most ${LIMITS.indicatorsMax} indicators.`);
  const levels = levelNames?.length ?? RESULT_LEVELS_MAX;
  for (const r of results) {
    if (r.levelNo > levels) problems.push(`${name(r)}: this logframe has only ${levels} levels.`);
    if (r.parentId === null) continue;
    const parent = byId.get(r.parentId);
    if (r.levelNo === 1) {
      problems.push(`${name(r)}: a result at the top level cannot sit under another result.`);
    } else if (!parent) {
      problems.push(`${name(r)}: its parent is not in the logframe.`);
    } else if (parent.levelNo >= r.levelNo) {
      problems.push(`${name(r)}: it can only sit under a result of a higher level.`);
    }
  }
  return problems;
}

export const evidenceInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("event"), id: uuid }).strict(),
  z.object({ kind: z.literal("deliverable"), id: uuid }).strict(),
  z
    .object({
      kind: z.literal("url"),
      url: httpUrlOrEmpty(LIMITS.url).refine((s) => s !== "", "Enter the web address."),
    })
    .strict(),
]);
export type EvidenceInput = z.infer<typeof evidenceInput>;

/** 79. POST /api/indicators/:id/readings */
export const createReadingBody = z
  .object({
    value: indicatorValue,
    readingDate: isoDate,
    note: optionalText(LIMITS.readingNote, "Note").default(""),
    evidence: evidenceInput.nullable().default(null),
  })
  .strict();
export type CreateReadingBody = z.input<typeof createReadingBody>;
export type CreateReadingInput = z.output<typeof createReadingBody>;

// ---------- Responses ----------

export type Evidence =
  | { kind: "event"; id: string; title: string }
  | { kind: "deliverable"; id: string; title: string }
  | { kind: "url"; url: string };

export type Reading = {
  id: string;
  value: number;
  readingDate: string;
  note: string;
  /** null also when the linked event/deliverable was deleted */
  evidence: Evidence | null;
  recordedBy: ActorRef;
  createdAt: string;
};

export type Indicator = {
  id: string;
  resultId: string;
  code: string;
  name: string;
  /** a few plain words; null = none yet (show the name) */
  shortName: string | null;
  unit: string;
  baseline: number | null;
  target: number | null;
  targetDate: string | null;
  meansOfVerification: string;
  autoSource: IndicatorAutoSource | null;
  /** R3: count, share from answers or reading of the outside world */
  kind: IndicatorKind;
  /** ARCHITECTURE 19.2; null when autoSource is null */
  autoValue: number | null;
  latest: Reading | null;
  /** ARCHITECTURE 19.3 */
  progressPct: number | null;
  /** newest first */
  readings: Reading[];
};

export type LogframeResult = {
  id: string;
  /** 1 = the top level */
  levelNo: number;
  /** the project's name of the level: "Specific objective" */
  levelName: string;
  code: string;
  statement: string;
  /** a few plain words; null = none yet (show the statement) */
  shortName: string | null;
  parentId: string | null;
  position: number;
  activities: { torId: string; activityCode: string; title: string; status: TorStatus }[];
  /** by position */
  indicators: Indicator[];
};

/** A result as a picker item or a link (TorDetail.result). */
export type LogframeResultRef = { id: string; levelNo: number; code: string; statement: string; shortName: string | null };

// ---------- Labels ----------

/** Cuts text to at most `max` characters on a word boundary, with an ellipsis. */
export function truncateText(text: string, max: number): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s,;:.\-–]+$/, "")}…`;
}

/**
 * The label of a result or an indicator in lists and pickers: its short name,
 * else its statement (or name) cut to about 60 characters. The code is not in
 * it: screens show the code next to it, in grey.
 */
export function logframeLabel(x: { shortName?: string | null; text: string }, max = 60): string {
  const short = x.shortName?.trim();
  return short ? short : truncateText(x.text, max);
}

/** The label with its code after it, for places that take plain text (a select option, a sentence). */
export function logframeLabelWithCode(x: { code?: string | null; shortName?: string | null; text: string }, max = 60): string {
  const label = logframeLabel(x, max);
  const code = x.code?.trim();
  return code ? `${label} (${code})` : label;
}

export type Logframe = {
  contract: { id: string; name: string; reference: string; status: ContractStatus };
  /** the project's names of its 2 to 5 result levels, top first */
  levelNames: string[];
  /** by position */
  results: LogframeResult[];
  /** lead + open / editor + open */
  can: { editStructure: boolean; recordReadings: boolean };
};

// ---------- Marius round 5, wave B: "feeds into", targets, baselines ----------

type FeedsIntoResult = { id: string; levelNo: number; code: string; statement?: string; parentId: string | null };

/**
 * LF1: the "feeds into" rule, as plain sentences per result. A result on a
 * level below the top feeds into a result exactly one level up, of the same
 * framework (`results` is the whole framework). It needs one when the
 * framework has any result on that level. The top level feeds into nothing.
 *
 * Edit results and the Logframe page refuse these problems when someone sets
 * a parent; the checks (LF14) show them as warnings for frameworks that came
 * in through an import.
 */
export function feedsIntoProblems(results: readonly FeedsIntoResult[], levelNames?: readonly string[]): { resultId: string; message: string }[] {
  const out: { resultId: string; message: string }[] = [];
  const byId = new Map(results.map((r) => [r.id, r]));
  const levelOf = (n: number) => (levelNames?.[n - 1] ?? `level ${n}`).toLowerCase();
  const name = (r: FeedsIntoResult) => r.code.trim() || (r.statement ? truncateText(r.statement, 40) : `A result at ${levelOf(r.levelNo)}`);
  for (const r of results) {
    if (r.levelNo === 1) {
      if (r.parentId) out.push({ resultId: r.id, message: `${name(r)} is at the top level, so it feeds into nothing.` });
      continue;
    }
    if (!r.parentId) {
      if (results.some((x) => x.levelNo === r.levelNo - 1)) out.push({ resultId: r.id, message: `Choose the ${levelOf(r.levelNo - 1)} that ${name(r)} feeds into.` });
      continue;
    }
    const parent = byId.get(r.parentId);
    if (!parent) out.push({ resultId: r.id, message: `${name(r)} feeds into a result that is not in this framework.` });
    else if (parent.levelNo !== r.levelNo - 1) out.push({ resultId: r.id, message: `${name(r)} can only feed into ${/^[aeiou]/.test(levelOf(r.levelNo - 1)) ? "an" : "a"} ${levelOf(r.levelNo - 1)} (one level up).` });
  }
  return out;
}

/** LF3: one part of a target's breakdown, "5 consultations". */
export const targetPartInput = z
  .object({
    label: requiredText(80, "Part"),
    value: indicatorValue,
  })
  .strict();

/**
 * LF3 (#15a): one target of an indicator. Value and date are optional, but a
 * target says something: a value, a date or a note. `final` marks the final
 * target (at most one; none marked = the last one). Parts: a breakdown that
 * should add up to the value (a warning when not, `targetBreakdownProblem`).
 */
export const indicatorTargetInput = z
  .object({
    value: indicatorValue.nullable().default(null),
    date: isoDate.nullable().default(null),
    label: optionalText(80, "Label").default(""),
    note: optionalText(LIMITS.meansOfVerification, "Note").default(""),
    /** What the value counts when it is not the indicator's unit ("new followers"); "" = the indicator's unit. */
    unit: optionalText(LIMITS.indicatorUnit, "Unit").default(""),
    /** What the breakdown is by: "type of event", "country". */
    breakdownBy: optionalText(80, "Broken down by").default(""),
    parts: z.array(targetPartInput).max(20, "A target can have at most 20 parts.").default([]),
    final: z.boolean().default(false),
  })
  .strict()
  .refine((t) => t.value !== null || t.date !== null || t.note !== "", { message: "Give the target a value, a date or a note.", path: ["value"] });
export type IndicatorTargetInput = z.input<typeof indicatorTargetInput>;

/** PUT /api/p/:id/logframe/indicators/:indicatorId/targets: every target, in order (replaces them). */
export const indicatorTargetsBody = z
  .object({ targets: z.array(indicatorTargetInput).max(12, "An indicator can have at most 12 targets.") })
  .strict()
  .refine((b) => b.targets.filter((t) => t.final).length <= 1, { message: "Mark one target as the final one.", path: ["targets"] });
export type IndicatorTargetsBody = z.input<typeof indicatorTargetsBody>;
export type IndicatorTargetsInput = z.output<typeof indicatorTargetsBody>;

/** Which of the targets is the final one: the one marked, else the last; -1 when there are none. */
export function finalTargetIndex(targets: readonly { final?: boolean; isFinal?: boolean }[]): number {
  const marked = targets.findIndex((t) => t.final || t.isFinal);
  return marked >= 0 ? marked : targets.length - 1;
}

/**
 * LF3: when a target's parts do not add up to its value, the sentence that
 * says so ("The parts add up to 18, not 20."); null when they do, when there
 * are no parts, or when the target has no value.
 */
export function targetBreakdownProblem(t: { value: number | null; parts: readonly { value: number }[]; label?: string }): string | null {
  if (t.value === null || t.parts.length === 0) return null;
  const sum = t.parts.reduce((s, p) => s + p.value, 0);
  if (Math.abs(sum - t.value) < 0.005) return null;
  const show = (n: number) => String(Math.round(n * 100) / 100);
  return `The parts of ${t.label ? `the target "${t.label}"` : "the target"} add up to ${show(sum)}, not ${show(t.value)}.`;
}
